import { useCallback, useMemo, useState } from 'react';

import { isMaterialConflict } from './promotionsUtils';
import type { ConflictChoice, PromotionChange, PromotionConflict } from './types';

/** The conflicts surfaced by a promotion apply/rollback and the hash they were reported under (echoed back
 * on the resolve as the stale-override guard). */
export interface ResolutionState {
  conflicts: PromotionConflict[];
  conflictHash: string | null;
}

/** Pull the conflicts + hash off any response that carries them — an approve/rollback report, a rollback
 * preview, or a stale re-report. Returns null when there are no conflicts (a clean / successful apply). */
export function resolutionFromConflicts(response: {
  conflicts?: unknown[] | null;
  conflict_detection_hash?: string | null;
}): ResolutionState | null {
  const conflicts = (response.conflicts ?? []) as PromotionConflict[];
  if (conflicts.length === 0) {
    return null;
  }

  return { conflicts, conflictHash: response.conflict_detection_hash ?? null };
}

export interface ConflictResolution {
  /** True once a report has surfaced conflicts — the dialog switches to the per-change resolution view. */
  inResolution: boolean;
  /** The reported hash, echoed back on the resolve as the stale-override guard. */
  conflictHash: string | null;
  /** change_id → conflict, for ChangeListByType's conflicts-first layout. */
  conflictMap: Record<string, PromotionConflict>;
  /** The reviewer's per-change keep/override choices so far. */
  decisions: Record<string, ConflictChoice>;
  /** Material conflicts (an override would clobber a value edited on the target) — each needs a decision. */
  materialConflictIds: string[];
  /** Benign conflicts (the change wouldn't actually overwrite the drift) — auto-applied, no decision. */
  benignConflictIds: string[];
  /** Count of material conflicts (what the resolution banner / confirm text count off). */
  conflictCount: number;
  /** The resolve payload: the material conflicts the reviewer overrode, plus every benign conflict. */
  overrideChangeIds: string[];
  /** True when every material conflict has an explicit decision (resolve is otherwise blocked). */
  allConflictsResolved: boolean;
  /** The target drifted AGAIN between report and resolve — nothing was applied; re-review. */
  isStaleRefresh: boolean;
  handleResolveConflict: (changeId: string, choice: ConflictChoice) => void;
  /** Enter (or refresh) the resolution view from a report's conflicts; `wasResolve` marks a re-drift stale. */
  enterResolution: (next: ResolutionState, options: { wasResolve: boolean }) => void;
  /** Clear all resolution state (on close / re-preview). */
  reset: () => void;
}

/** Shared approve/rollback conflict-resolution state. Both flows report the same per-change conflict shape
 * and resolve it identically (per-change keep/override + a stale-hash re-report), differing only in the
 * copy around it — so the material/benign split, the override payload, and the "all resolved" gate live
 * here rather than being re-derived per dialog. `changes` supplies each conflict's would-be new value
 * (`after_value`), used to tell a material conflict (an override clobbers an edit) from a benign one. */
export function useConflictResolution(changes: PromotionChange[]): ConflictResolution {
  const [resolution, setResolution] = useState<ResolutionState | null>(null);
  const [decisions, setDecisions] = useState<Record<string, ConflictChoice>>({});
  // The target drifted AGAIN between report and resolve (stale-hash guard): the conflicts were just
  // refreshed and nothing was applied — the reviewer must re-review.
  const [isStaleRefresh, setIsStaleRefresh] = useState(false);

  const inResolution = resolution !== null;

  const conflictMap = useMemo<Record<string, PromotionConflict>>(
    () => Object.fromEntries((resolution?.conflicts ?? []).map((c) => [c.change_id, c])),
    [resolution]
  );

  // The per-change record, so we can tell a *material* conflict (an override would clobber a value
  // someone changed on the target) from a benign one (the drift wouldn't actually be overwritten).
  const changeByChangeId = useMemo(() => {
    const map: Record<string, PromotionChange> = {};
    for (const change of changes) {
      map[change.change_id] = change;
    }
    return map;
  }, [changes]);

  // Split detected conflicts: material ones need an explicit decision; benign ones (no field the change
  // would actually overwrite) apply automatically like any non-conflicting change.
  const [materialConflictIds, benignConflictIds] = useMemo(() => {
    const material: string[] = [];
    const benign: string[] = [];
    for (const conflict of resolution?.conflicts ?? []) {
      // A conflict whose change is absent from the change list has no resolution dropdown to satisfy
      // (ChangeListByType renders a decision only for conflicts found in `changes`). Ignore it rather than
      // counting it toward `allConflictsResolved` — otherwise it would leave resolve permanently disabled.
      // The backend keeps conflict change_ids ⊆ changes; this guards that invariant.
      const change = changeByChangeId[conflict.change_id];
      if (change === undefined) {
        continue;
      }
      // Same predicate the render uses to decide which conflicts get a card, so the payload's benign
      // (auto-override) bucket can never include a conflict the reviewer was shown and chose to keep.
      (isMaterialConflict(change, conflict) ? material : benign).push(conflict.change_id);
    }
    return [material, benign];
  }, [resolution, changeByChangeId]);

  const conflictCount = materialConflictIds.length;

  // Resolve payload: the material conflicts the reviewer chose to override, plus every benign conflict — a
  // conflicting change is skipped unless its id is in this list, so benign ones must be included to apply.
  const overrideChangeIds = useMemo(
    () => [...materialConflictIds.filter((id) => decisions[id] === 'override'), ...benignConflictIds],
    [materialConflictIds, benignConflictIds, decisions]
  );

  // Resolve is blocked until every *material* conflict has an explicit decision (no implicit default).
  const allConflictsResolved = inResolution && materialConflictIds.every((id) => decisions[id] !== undefined);

  // Stable identities (only touch state setters) so consumers can call them from an effect — the rollback
  // dialog enters resolution from its preview effect, which would loop on a per-render callback.
  const handleResolveConflict = useCallback((changeId: string, choice: ConflictChoice) => {
    setDecisions((prev) => ({ ...prev, [changeId]: choice }));
  }, []);

  const enterResolution = useCallback((next: ResolutionState, { wasResolve }: { wasResolve: boolean }) => {
    setResolution(next);
    setDecisions({}); // values changed underfoot → make the reviewer re-pick every conflict
    setIsStaleRefresh(wasResolve); // a resolve that came back with conflicts means the target re-drifted
  }, []);

  const reset = useCallback(() => {
    setResolution(null);
    setDecisions({});
    setIsStaleRefresh(false);
  }, []);

  return {
    inResolution,
    conflictHash: resolution?.conflictHash ?? null,
    conflictMap,
    decisions,
    materialConflictIds,
    benignConflictIds,
    conflictCount,
    overrideChangeIds,
    allConflictsResolved,
    isStaleRefresh,
    handleResolveConflict,
    enterResolution,
    reset,
  };
}
